import { Boxes, Globe2, RefreshCw, Search, ShieldAlert, Wrench } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { ModuleIcon } from '@/features/modulos/components/module-icon';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { normalizeSearch } from '@/lib/search';
import { cn } from '@/lib/utils';

import { useCatalogoModulos } from '../planes.api';
import type { ModuloSistema } from '../planes.types';

const SELECT_CLASSES =
  'h-10 rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 transition-colors ' +
  'focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Mensaje de error legible. */
function mensajeError(error: unknown): string {
  if (error instanceof ApiError) return error.message;

  return 'No se pudo cargar el catálogo de módulos.';
}

/** Tarjeta de un módulo del sistema. */
function TarjetaModulo({ modulo }: { modulo: ModuloSistema }) {
  return (
    <article className="flex flex-col rounded-card border border-ink-100 bg-white p-3 shadow-soft">
      <div className="flex items-start gap-2.5">
        <span
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-xl ring-1',
            'bg-brand-50 text-brand-600 ring-brand-100',
          )}
          aria-hidden="true"
        >
          <ModuleIcon name={modulo.icono_web} className="size-4.5" />
        </span>

        <div className="min-w-0 flex-1">
          <h3 className="truncate text-sm font-semibold text-ink-900" title={modulo.nombre}>
            {modulo.nombre}
          </h3>
          <p className="truncate font-mono text-[11px] text-ink-400" title={modulo.codigo}>
            {modulo.codigo}
          </p>
        </div>
      </div>

      {modulo.descripcion ? (
        <p className="mt-2 line-clamp-2 text-[12px] leading-relaxed text-ink-500">
          {modulo.descripcion}
        </p>
      ) : null}

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {!modulo.activo ? <Badge variant="warning">Inactivo</Badge> : null}
        {modulo.web_path ? (
          <Badge variant="brand" title={modulo.web_path}>
            EDSuite Web
          </Badge>
        ) : (
          <Badge variant="outline">Panel anterior</Badge>
        )}
        {modulo.visible_web ? <Badge variant="success">Visible</Badge> : null}
        {modulo.sistema === 'LICENCIA' ? <Badge variant="neutral">Licencia</Badge> : null}
        {modulo.condicion ? (
          <Badge variant="outline" title="Condición de visibilidad">
            {modulo.condicion}
          </Badge>
        ) : null}
      </div>

      <dl className="mt-2 space-y-0.5 text-[11px] text-ink-500">
        <div className="flex items-center gap-1.5">
          <ShieldAlert className="size-3 shrink-0" aria-hidden="true" />
          <dt className="sr-only">Grupo requerido</dt>
          <dd className="truncate">
            grupo: {modulo.grupo_permiso ?? '—'}
            {modulo.grupo_extra ? ` + ${modulo.grupo_extra}` : ''}
          </dd>
        </div>
        <div className="flex items-center gap-1.5">
          <Globe2 className="size-3 shrink-0" aria-hidden="true" />
          <dt className="sr-only">Ruta</dt>
          <dd className="truncate" title={modulo.ruta ?? ''}>
            {modulo.ruta ?? 'sin ruta'}
          </dd>
        </div>
      </dl>
    </article>
  );
}

/**
 * Módulos del Sistema (`/configuracion/modulos`).
 *
 * Puerto de `Planes::modulos()`: el catálogo completo de módulos agrupado por
 * categoría, con su ruta, el grupo que exige y si ya está publicado en EDSuite
 * Web. Es la vista que explica de dónde sale el menú del panel.
 */
export function ModulosPage() {
  useDocumentTitle('Configuración · Módulos del sistema');

  const [buscar, setBuscar] = useState('');
  const [categoria, setCategoria] = useState('todas');

  const catalogo = useCatalogoModulos();

  const modulos = catalogo.data?.modulos ?? [];
  const categorias = catalogo.data?.categorias ?? [];

  const sinPermiso = catalogo.error instanceof ApiError && catalogo.error.status === 403;

  /** Nombre de la categoría a partir de su código (la caja puede variar). */
  const nombreCategoria = (codigo: string) =>
    categorias.find((item) => item.codigo.toLowerCase() === codigo.toLowerCase())?.nombre ?? codigo;

  const grupos = useMemo(() => {
    const termino = normalizeSearch(buscar.trim());
    const lista = catalogo.data?.modulos ?? [];
    const mapa = new Map<string, ModuloSistema[]>();

    for (const modulo of lista) {
      if (categoria !== 'todas' && modulo.categoria.toLowerCase() !== categoria) continue;

      if (
        termino &&
        !normalizeSearch(
          `${modulo.nombre} ${modulo.codigo} ${modulo.grupo_permiso ?? ''} ${modulo.ruta ?? ''}`,
        ).includes(termino)
      ) {
        continue;
      }

      const clave = modulo.categoria.toLowerCase();
      mapa.set(clave, [...(mapa.get(clave) ?? []), modulo]);
    }

    return [...mapa.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [catalogo.data, buscar, categoria]);

  const visibles = grupos.reduce((total, [, lista]) => total + lista.length, 0);
  const migrados = modulos.filter((modulo) => Boolean(modulo.web_path)).length;

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        <header>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Configuración</p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <Boxes className="size-6 text-brand-500" aria-hidden="true" />
            Módulos del sistema
          </h1>
        </header>

        <Alert variant="warning" title="Acceso restringido">
          Solo el administrador principal puede ver la configuración de planes y módulos.
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Configuración</p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <Boxes className="size-6 text-brand-500" aria-hidden="true" />
            Módulos del sistema
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-500">
            Catálogo completo de módulos: es la fuente del menú del panel (categorías, rutas,
            grupo requerido y si ya está publicado en EDSuite Web).
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void catalogo.refetch()}
            loading={catalogo.isFetching}
          >
            <RefreshCw className="size-3.5" aria-hidden="true" />
            Actualizar
          </Button>
        </div>
      </header>

      <SectionCard
        title="Catálogo de módulos"
        description={`${modulos.length} módulo(s) en ${categorias.length} categoría(s) · ${migrados} publicado(s) en EDSuite Web`}
        action={
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 text-xs font-medium text-ink-500">
              Categoría
              <Select
                value={categoria}
                onChange={(event) => setCategoria(event.target.value)}
                className={SELECT_CLASSES}
              >
                <option value="todas">Todas</option>
                {categorias.map((item) => (
                  <option key={item.codigo} value={item.codigo.toLowerCase()}>
                    {item.nombre} ({item.total_modulos})
                  </option>
                ))}
              </Select>
            </label>

            <div className="w-56">
              <Input
                leftIcon={<Search className="size-4" aria-hidden="true" />}
                placeholder="Buscar módulo…"
                value={buscar}
                onChange={(event) => setBuscar(event.target.value)}
              />
            </div>
          </div>
        }
      >
        {catalogo.isPending ? (
          <Skeleton className="h-96 rounded-card" />
        ) : catalogo.isError ? (
          <Alert variant="error" title="No se pudo cargar el catálogo">
            <div className="flex flex-wrap items-center gap-3">
              <span>{mensajeError(catalogo.error)}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => void catalogo.refetch()}
                loading={catalogo.isFetching}
              >
                Reintentar
              </Button>
            </div>
          </Alert>
        ) : visibles === 0 ? (
          <div className="rounded-control border border-dashed border-ink-200 px-4 py-12 text-center">
            <Wrench className="mx-auto size-5 text-ink-300" aria-hidden="true" />
            <p className="mt-2 text-sm font-medium text-ink-700">
              Ningún módulo coincide con el filtro.
            </p>
          </div>
        ) : (
          <div className="space-y-5">
            {grupos.map(([codigo, lista]) => (
              <section key={codigo}>
                <header className="mb-2 flex flex-wrap items-center gap-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-600">
                    {nombreCategoria(codigo)}
                  </h3>
                  <Badge variant="outline">{lista.length} módulo(s)</Badge>
                </header>

                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {lista.map((modulo) => (
                    <TarjetaModulo key={modulo.codigo} modulo={modulo} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
